import {Box, useOutsideClick} from '@chakra-ui/react';
import {useSearchAutocomplete} from 'api/queries';
import {SearchInput} from 'components/shared';
import {useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import {ISearchAutocompleteItem, ProviderTypeEnum} from 'types';
import {preparedSearchSuggestions} from 'utils/search';

interface IProps {
  providerType?: ProviderTypeEnum;
  heightLG?: boolean;
}

export const GlobalSearch: FC<IProps> = React.memo(({providerType, heightLG}) => {
  const {push, query} = useRouter();
  const searchQuery = query.query as string;
  const [searchValue, setSearchValue] = useState(searchQuery ?? '');
  const [shouldFetchData, toggleDataFetchingState] = useState(false);
  const {t} = useTranslation();
  const ref = useRef<HTMLDivElement>(null);

  const {
    data: autocompleteData,
    refetch: fetchSearchAutocompleteData,
    isStale,
  } = useSearchAutocomplete(providerType, {
    onSuccess: () => {
      toggleDataFetchingState(false);
    },
  });

  useOutsideClick({
    ref,
    handler: () => toggleDataFetchingState(false),
  });

  useEffect(() => {
    if (shouldFetchData && isStale) {
      fetchSearchAutocompleteData();
    }
  }, [providerType, fetchSearchAutocompleteData, shouldFetchData, isStale]);

  // const isRestaurant = useMemo(() => providerType === ProviderTypeEnum.RESTAURANT, [providerType]);

  const preparedSuggestions = useMemo(() => {
    const searchValueSuggestion = {
      type: 'search',
      name: `${t('SEARCH')} "${searchValue}"`,
      provider_type: providerType,
      slug: '',
    } as ISearchAutocompleteItem;
    return preparedSearchSuggestions(searchValue, searchValueSuggestion, autocompleteData);
  }, [t, searchValue, providerType, autocompleteData]);

  const placeholder = useMemo(() => {
    // return isRestaurant ? t('SEARCH_INPUT_FOR_RESTAURANTS') : t('SEARCH_INPUT_FOR_MARKETS');
    return t('SEARCH');
  }, [t]);

  const handleItemClick = React.useCallback(
    (item: ISearchAutocompleteItem) => {
      if (item?.type === 'search') {
        push(`/search?query=${searchValue}`);
      } else if (item?.type === 'provider') {
        push(`/restaurant/${item.slug}`);
      } else if (item?.type === 'category') {
        push(
          `${
            item.provider_type === 'restaurant'
              ? `/category/${item.slug}`
              : `/${item.provider_type}s?categorySlug=${item.slug}&products=true`
          }`,
        );
      }
    },
    [push, searchValue],
  );

  return (
    <Box onFocus={() => toggleDataFetchingState(true)}>
      <SearchInput
        placeholder={placeholder}
        value={searchValue}
        onChange={setSearchValue}
        suggestions={preparedSuggestions}
        onItemClick={handleItemClick}
        heightLG={heightLG}
      />
    </Box>
  );
});
